import { useState, useCallback, useMemo } from 'react';
import { NavMarker } from 'react-rhumb';
import styled from 'styled-components';
import { useSidebarCollapsed } from '../../../hooks/useSidebarCollapsed';
import StyledRightSidebar from './StyledRightSidebar';
import FormattedMessage from 'I18n/components/FormattedMessage';
import CrmLogger from 'customer-data-tracking/loggers/CrmLogger';
import I18n from 'I18n';
import { IconButton } from 'foundations-components/transitional/components/IconButton';
import { LastIcon } from 'foundations-assets';
import { SCROLLABLE_TABS } from '../../../../constants/gates';
import AuthContainer from '../../../../containers/AuthContainer';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const ButtonWrapper = styled.div.withConfig({
  displayName: "CollapsibleRightSidebar__ButtonWrapper",
  componentId: "rjwwry-0"
})(["", ""], ({
  isScrollableTabsEnabled,
  theme
}) => `
  position: absolute;
  left: ${isScrollableTabsEnabled ? '-16px' : '-24px'};
  top: ${isScrollableTabsEnabled ? '95px' : '32px'};
  z-index: 1;
  border-radius: ${theme.borderRadius['full']};
  background-color: ${theme.color['fill-surface-default']};

  [data-collapsed='true'] & {
    transform: rotate(-180deg);
  }

  @media (prefers-reduced-motion: no-preference) {
    /* Overrides default transition styles applied to the button */
    transition-delay: 50ms !important;
    transition: transform 350ms ease-in-out !important;
  }
  `);
const CollapsibleRightSidebar = ({
  children,
  objectType
}) => {
  const [isCollapsed, toggleCollapse] = useSidebarCollapsed();
  const [tooltipOpenOverride, setTooltipOpenOverride] = useState(undefined);
  const auth = AuthContainer.get();
  const gates = auth.gates;
  const isScrollableTabsEnabled = useMemo(() => gates.includes(SCROLLABLE_TABS), [gates]);
  const trackEvent = useCallback(() => {
    if (objectType) {
      CrmLogger.logRecordInteraction(objectType, {
        action: 'Expand/collapse right sidebar',
        subAction: isCollapsed ? 'expand' : 'collapse'
      });
    }
  }, [objectType, isCollapsed]);
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [isCollapsed && /*#__PURE__*/_jsx(NavMarker, {
      name: "RIGHT_SIDEBAR_LOADED_COLLAPSED"
    }), /*#__PURE__*/_jsxs(StyledRightSidebar, {
      collapsedWidth: 20,
      "data-collapsed": isCollapsed,
      "data-test-id": "records-right-sidebar",
      children: [/*#__PURE__*/_jsx(ButtonWrapper, {
        isScrollableTabsEnabled: isScrollableTabsEnabled,
        children: /*#__PURE__*/_jsx(IconButton, {
          onClick: () => {
            toggleCollapse();
            trackEvent();
            // Force the tooltip closed on toggle to prevent it from sticking around when the button is in a different location on the screen
            setTooltipOpenOverride(false);
            // Set the tooltip override back to undefined in the next frame, so the tooltip is in charge of its open state again
            // Must happen in a separate render or React will reconcile both state updates to only do the most recent one, aka not change the tooltip override at all
            requestAnimationFrame(() => {
              setTooltipOpenOverride(undefined);
            });
          },
          placement: "left",
          shape: "circle",
          size: "sm",
          tooltip: /*#__PURE__*/_jsx(FormattedMessage, {
            message: isCollapsed ? 'crm-records-ui.collapsibleSidebar.show' : 'crm-records-ui.collapsibleSidebar.hide'
          }),
          tooltipProps: {
            open: tooltipOpenOverride
          },
          use: "tertiary",
          "data-test-id": "toggle-sidebar-collapse",
          "aria-label": I18n.text(isCollapsed ? 'crm-records-ui.collapsibleSidebar.ariaShow' : 'crm-records-ui.collapsibleSidebar.ariaHide'),
          children: /*#__PURE__*/_jsx(LastIcon, {})
        })
      }), isCollapsed ? null : children]
    })]
  });
};
export default CollapsibleRightSidebar;